import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Circle, Umbrella, Bird, Fish, Bus, Ship, Landmark, Anchor, Music, Waves,
  Sunset, Home, MessageCircle, GlassWater, MapPin, Route, Building2, IceCream2,
  Camera, Heart, Check, Share2, Palmtree, Flower2, Trophy, RotateCcw, X,
} from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal, MaskedLines } from "@/components/motion-primitives";
import { usePageMeta } from "@/components/shared";
import { track } from "@/lib/transport";

const CHALLENGES = [
  [Circle, "Walk through a Bermuda moongate"],
  [Umbrella, "Visit a pink-sand beach"],
  [Bird, "Spot a Bermuda longtail"],
  [Fish, "Try a Bermuda fish sandwich"],
  [Bus, "Ride a pink Bermuda bus"],
  [Ship, "Take the ferry"],
  [Landmark, "Visit St. George's"],
  [Anchor, "Explore Royal Naval Dockyard"],
  [Music, "Respectfully photograph a Gombey"],
  [Waves, "Swim or snorkel"],
  [Sunset, "Watch a Bermuda sunset"],
  [Home, "Find a Bermuda roof you love"],
  [MessageCircle, "Learn a Bermudian saying"],
  [GlassWater, "Try a local Bermuda drink"],
  [MapPin, "Find a hidden beach"],
  [Route, "Explore a Bermuda coastal trail"],
  [Building2, "Explore Hamilton"],
  [IceCream2, "Try a local sweet treat"],
  [Camera, "Photograph a colorful Bermuda scene"],
  [Heart, "Find your favorite place in Bermuda"],
];

const MILESTONES = [
  { at: 10, name: "Island Explorer", icon: Palmtree, message: "Ten experiences down — you're officially more than a beach-chair tourist." },
  { at: 15, name: "Bermuda Insider", icon: Flower2, message: "Fifteen! You know this island better than most visitors ever will." },
  { at: 20, name: "Bucket List Legend", icon: Trophy, message: "You came. You explored. You experienced Bermuda." },
];

const STORAGE_KEY = "bch_bucket_v1";

export default function BucketList() {
  usePageMeta("The Bermuda Bucket List Challenge", "Take on The Bermuda Bucket List Challenge and discover Bermuda through beaches, food, culture, landmarks and unforgettable local experiences.");
  const [checked, setChecked] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); } catch { return new Set(); }
  });
  const [celebration, setCelebration] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const count = checked.size;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...checked]));
  }, [checked]);

  const toggle = (i) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      for (const m of MILESTONES) {
        if (prev.size < m.at && next.size === m.at) {
          setCelebration(m);
          track("bucket_list_milestone", { milestone: m.at });
        }
      }
      track("bucket_list_toggled", { index: i, completed: next.has(i), total: next.size });
      return next;
    });
  };

  const share = async () => {
    const text = `I've completed ${count}/20 challenges on The Bermuda Bucket List! 🇧🇲 How many can you complete? @TheBermudaCoHost #BermudaBucketList #TheBermudaCoHost`;
    track("bucket_list_share", { count });
    if (navigator.share) {
      try { await navigator.share({ title: "The Bermuda Bucket List Challenge", text, url: window.location.href }); } catch (e) { /* dismissed */ }
    } else {
      try { await navigator.clipboard.writeText(`${text} ${window.location.href}`); } catch (e) { /* ignore */ }
      setCelebration({ at: 0, name: "Copied!", icon: Share2, message: "Your progress message is on the clipboard — paste it anywhere." });
    }
  };

  const reset = () => {
    setChecked(new Set());
    setConfirmReset(false);
    track("bucket_list_reset");
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy text-ivory">
        <div className="absolute inset-0">
          <img src={IMAGES.beach} alt="Horseshoe Bay, Bermuda" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy" />
        </div>
        <div className="noise-overlay relative z-10 mx-auto max-w-[1100px] px-6 pb-16 pt-40 text-center lg:px-10">
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">A Guest Challenge</p>
          </Reveal>
          <MaskedLines
            lines={["The Bermuda", "Bucket List Challenge"]}
            className="mx-auto mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
          />
          <Reveal delay={0.4}>
            <p className="mx-auto mt-6 max-w-lg text-base text-ivory/75">
              How much of Bermuda can you experience before checkout?
            </p>
          </Reveal>

          {/* Progress card */}
          <Reveal delay={0.55}>
            <div className="mx-auto mt-10 max-w-md border border-white/15 bg-white/5 p-6 backdrop-blur-md" data-testid="bucket-progress-card">
              <p className="font-mono text-2xl tracking-tight" data-testid="bucket-progress-count">
                {count} <span className="text-ivory/50">/ 20</span>{" "}
                <span className="text-xs uppercase tracking-[0.25em] text-ivory/60">completed</span>
              </p>
              <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-white/15" role="progressbar" aria-valuenow={count} aria-valuemin={0} aria-valuemax={20} aria-label="Challenge progress">
                <motion.div
                  className="h-full rounded-full bg-bermuda"
                  animate={{ width: `${(count / 20) * 100}%` }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              <div className="mt-5 flex items-center justify-center gap-6">
                {MILESTONES.map((m) => (
                  <div key={m.at} className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider transition-colors ${count >= m.at ? "text-bermuda" : "text-ivory/35"}`} data-testid={`milestone-chip-${m.at}`}>
                    <m.icon size={13} /> {m.at}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Challenge board */}
      <section className="mx-auto max-w-[1440px] px-6 py-20 lg:px-10" data-testid="bucket-board">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CHALLENGES.map(([Icon, label], i) => {
            const done = checked.has(i);
            return (
              <button
                key={i}
                onClick={() => toggle(i)}
                aria-pressed={done}
                data-testid={`challenge-${i}`}
                className={`group flex items-center gap-4 border p-5 text-left transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C48B7F] ${
                  done
                    ? "border-bermuda-dark bg-pink-soft/60 shadow-sm"
                    : "border-navy/10 bg-white hover:border-bermuda hover:shadow-lg hover:shadow-navy/5"
                }`}
              >
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors ${done ? "bg-bermuda-dark text-white" : "bg-sand-light text-navy/60 group-hover:text-bermuda-dark"}`}>
                  {done ? <Check size={18} strokeWidth={2.5} /> : <Icon size={18} strokeWidth={1.5} />}
                </span>
                <span className={`text-sm leading-snug transition-colors ${done ? "font-medium text-navy" : "text-navy/70"}`}>{label}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          {confirmReset ? (
            <span className="inline-flex items-center gap-4 border border-navy/15 bg-white px-6 py-3.5 text-sm" data-testid="bucket-reset-confirm">
              <span className="text-navy/70">Reset all 20 challenges?</span>
              <button onClick={reset} data-testid="bucket-reset-yes" className="font-semibold text-bermuda-dark hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F]">Yes, reset</button>
              <button onClick={() => setConfirmReset(false)} data-testid="bucket-reset-no" className="text-navy/50 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F]">Keep my progress</button>
            </span>
          ) : (
            <button onClick={() => setConfirmReset(true)} data-testid="bucket-reset-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-navy/40 transition-colors hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F]">
              <RotateCcw size={12} /> Reset My Challenge
            </button>
          )}
        </div>
      </section>

      {/* Share */}
      <section className="bg-sand-light">
        <div className="mx-auto max-w-[900px] px-6 py-20 text-center lg:px-10" data-testid="bucket-share">
          <Reveal>
            <h2 className="font-serif text-3xl font-light text-navy sm:text-4xl">Share Your Bermuda Adventure</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-navy/60">
              Show us how you're experiencing Bermuda. Share your favorite challenge moments and tag{" "}
              <strong className="text-navy">@TheBermudaCoHost</strong>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-4 font-mono text-xs tracking-wide text-bermuda-dark">#BermudaBucketList · #TheBermudaCoHost · #Bermuda · #BermudaVacation</p>
          </Reveal>
          <Reveal delay={0.2}>
            <button
              onClick={share}
              data-testid="bucket-share-btn"
              className="mt-8 inline-flex items-center gap-2.5 bg-navy px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bermuda"
            >
              <Share2 size={14} /> Share My Progress
            </button>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-ivory">
        <div className="noise-overlay mx-auto max-w-[1100px] px-6 py-20 text-center lg:px-10">
          <Reveal>
            <h2 className="font-serif text-3xl font-light sm:text-4xl">Discover Bermuda with The Bermuda Co-Host</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-4 max-w-md text-sm text-ivory/65">Make your Bermuda stay effortless from check-in to checkout.</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/services" data-testid="bucket-cta-services" className="bg-bermuda px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory">Explore Our Services</Link>
              <Link to="/guest-tools/plan-your-stay" data-testid="bucket-cta-plan" className="border border-ivory/40 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ivory hover:text-navy">Plan Your Stay</Link>
              <Link to="/contact" data-testid="bucket-cta-contact" className="border border-ivory/40 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ivory hover:text-navy">Contact The Bermuda Co-Host</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Celebration overlay */}
      <AnimatePresence>
        {celebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/80 p-6 backdrop-blur-sm"
            onClick={() => setCelebration(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Milestone achieved"
            data-testid="bucket-celebration"
          >
            <motion.div
              initial={{ scale: 0.85, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="relative w-full max-w-md bg-ivory p-8 text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setCelebration(null)} aria-label="Close" data-testid="bucket-celebration-close" className="absolute right-4 top-4 text-navy/40 hover:text-navy">
                <X size={20} />
              </button>
              {celebration.at === 20 ? (
                <img src="/images/bucket-list-legend.jpg" alt="Bucket List Legend completion card" className="mx-auto w-full max-w-xs shadow-lg" data-testid="bucket-legend-card" />
              ) : (
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-pink-soft text-bermuda-dark">
                  <celebration.icon size={28} strokeWidth={1.5} />
                </span>
              )}
              <h3 className="mt-6 font-serif text-3xl text-navy" data-testid="bucket-celebration-title">{celebration.name}</h3>
              <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-navy/60">{celebration.message}</p>
              {celebration.at === 20 && (
                <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-bermuda-dark">You completed the Bermuda Bucket List!</p>
              )}
              <button
                onClick={() => setCelebration(null)}
                data-testid="bucket-celebration-continue"
                className="mt-7 bg-navy px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark"
              >
                {celebration.at === 20 ? "Legendary." : "Keep Going"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
